import { fechaCorta, fechaYHora, soloFecha } from '../lib/fechas';

/**
 * La fecha comprometida de un caso. **Una sola, con un nombre, en todas las pantallas.**
 *
 * **Por qué existe (2026-10-09).** La misma fecha tenía cuatro nombres —«Hasta», «Comprometido»,
 * «vence», «Compromiso → Hasta»— y en el Tablero no estaba: quien la veía en Mis solicitudes no la
 * encontraba en el caso. Y cada pantalla pintaba su propio semáforo a reloj de pared, así que un
 * caso en un estado que detiene el reloj se veía vencido.
 *
 * Todo lo que muestra sale de `caso.compromiso`, que calcula `relojes.compromiso_vigente` en el
 * backend: la fecha vigente (corrida por las pausas cuando la calculó el SLA), el semáforo y **la
 * frase que dice de dónde salió**. Esta pantalla no redacta nada por su cuenta.
 */
const PINTA = {
  breach: ['bg-[#FF2A00]', 'Vencida'],
  risk: ['bg-[#FFB800]', 'Por vencer'],
  ok: ['bg-[#00D15C]', 'En tiempo'],
  en_pausa: ['bg-zinc-300', 'En pausa'],
};

export const ROTULO_FECHA_COMPROMETIDA = 'Fecha comprometida';

function Punto({ semaforo }) {
  const pinta = PINTA[semaforo];
  if (!pinta) return null;
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${pinta[0]}`} />;
}

/** Lo que dice la celda cuando no hay fecha: en pausa, sin plazo, o no calculable. */
function sinFecha(c) {
  if (c?.semaforo === 'en_pausa') return 'en pausa';
  if (c?.origen === 'no_calculable') return 'no calculable';
  // Un pedido planificado que nadie planificó todavía: no es «sin plazo», es «todavía no».
  if (c?.origen === 'a_planificar') return 'a planificar';
  return 'sin plazo';
}

/** Para una celda de tabla: el punto, la fecha corta, y la frase de origen en el tooltip. */
export function FechaComprometidaCorta({ caso }) {
  const c = caso?.compromiso;
  if (!c) return <span className="text-zinc-400">—</span>;
  return (
    <span
      className={`inline-flex items-center gap-1 ${c.origen === 'no_calculable' ? 'text-amber-700' : 'text-zinc-600'}`}
      title={c.frase}
      data-testid={`fecha-comprometida-${caso.ticket_id}`}
    >
      <Punto semaforo={c.semaforo} />
      {c.fecha ? fechaCorta(c.fecha, null) : <span className="text-zinc-400">{sinFecha(c)}</span>}
    </span>
  );
}

const VEREDICTO = {
  entra: (m) => `entra${m != null ? ` (${m} días hábiles de margen)` : ''}`,
  no_entra: (m) => `no entra${m != null ? ` (${Math.abs(m)} días hábiles tarde)` : ''}`,
  sin_plan: () => 'todavía sin plan',
  sin_compromiso: () => 'no hay fecha comprometida contra la cual comparar',
};

/**
 * Para la cabecera del caso: el rótulo, la fecha con su estado, la frase de origen y, si viene,
 * el plan contra el compromiso. Va arriba de las pestañas para que se vea igual desde el Detalle y
 * desde el Tablero.
 */
export default function FechaComprometida({ caso, acciones = null }) {
  const c = caso?.compromiso;
  if (!c) return null;
  const pinta = PINTA[c.semaforo];
  const plan = c.plan;
  return (
    <div className="text-xs" data-testid="fecha-comprometida">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500">{ROTULO_FECHA_COMPROMETIDA}</span>
        <Punto semaforo={c.semaforo} />
        <span className={`font-medium ${c.origen === 'no_calculable' ? 'text-amber-700' : 'text-zinc-800'}`}>
          {c.fecha ? fechaYHora(c.fecha) : sinFecha(c)}
        </span>
        {pinta && <span className="text-zinc-500">· {pinta[1]}</span>}
        {acciones}
      </div>
      <p className="text-[11px] text-zinc-500 mt-0.5">{c.frase}</p>
      {plan && (
        <p className="text-[11px] text-zinc-500 mt-0.5" data-testid="plan-vs-compromiso">
          <span className="text-zinc-600">Plan:</span>{' '}
          {plan.plan_end ? `termina el ${soloFecha(plan.plan_end)}, ` : ''}
          {(VEREDICTO[plan.estado] || (() => plan.estado))(plan.margen_dias)}
        </p>
      )}
    </div>
  );
}
